/**
 * Development-only logging utility
 *
 * All log methods (log, info, warn, error) only output in development mode.
 * In production, all logs are silent to keep console clean and avoid leaking info.
 *
 * Usage:
 * ```typescript
 * import { createLogger } from '@/utils/logger';
 * const logger = createLogger('MyComponent');
 *
 * logger.log("🔄 Processing data", { count: 5 });
 * logger.error("❌ Failed to process", error);
 * ```
 */

import { isDevelopment } from "@/utils/environment";

class Logger {
  private namespace?: string;

  constructor(namespace?: string) {
    this.namespace = namespace;
  }

  private format(args: any[]): any[] {
    if (this.namespace) {
      return [`[${this.namespace}]`, ...args];
    }
    return args;
  }

  log(...args: any[]) {
    if (isDevelopment) {
      console.log(...this.format(args));
    }
  }

  info(...args: any[]) {
    if (isDevelopment) {
      console.info(...this.format(args));
    }
  }

  warn(...args: any[]) {
    if (isDevelopment) {
      console.warn(...this.format(args));
    }
  }

  error(...args: any[]) {
    if (isDevelopment) {
      console.error(...this.format(args));
    }
  }
}

/**
 * Default logger without namespace
 */
export const logger = new Logger();

/**
 * Create a namespaced logger for better log organization
 *
 * @param namespace - Module or component name (e.g., 'AgentPolling', 'ChatStatus')
 * @returns Logger instance with namespace prefix
 */
export const createLogger = (namespace: string) => new Logger(namespace);
